/**
 * Demo-only fallback data for the Charts Lab.
 *
 * The lab prefers LIVE data from /api/kpi/dashboard (Snowflake). This fixture is
 * used only when that feed is unavailable (no backend, or the viewer lacks
 * kpi.read) so the AG Charts vs recharts comparison still renders. It is clearly
 * labeled "sample data" in the UI and must never be mistaken for real numbers.
 *
 * `buildDatasets` reads only a subset of KpiDashboardResponse, so this partial is
 * cast to the full type rather than reproducing every KPI field.
 */
import type { KpiDashboardResponse, KpiProductItem } from "@/features/kpi/api";

function ymMinus(anchor: string, back: number): string {
  const [ys, ms] = anchor.split("-");
  let y = Number(ys);
  let m = Number(ms) - back;
  while (m <= 0) {
    m += 12;
    y -= 1;
  }
  return `${y}-${String(m).padStart(2, "0")}`;
}

function monthsEndingAt(anchor: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => ymMinus(anchor, count - 1 - i));
}

/** Deterministic rising series (no RNG) so the demo is stable across renders. */
function series(base: number, months: number, growth: number): number[] {
  const out: number[] = [];
  let v = base;
  for (let i = 0; i < months; i++) {
    out.push(Math.round(v));
    v *= growth;
  }
  return out;
}

const ANCHOR = "2026-07";

const PRODUCT_MONTHS = monthsEndingAt(ANCHOR, 8);

const PRODUCTS: KpiProductItem[] = [
  { key: "core", name: "Core Platform", color: "brand", arr: series(5_400_000, 8, 1.018), accounts: series(820, 8, 1.006).map(Math.round), note: null },
  { key: "reviews", name: "Reviews", color: "power", arr: series(1_650_000, 8, 1.022), accounts: series(410, 8, 1.008).map(Math.round), note: null },
  { key: "messaging", name: "Messaging", color: "purple", arr: series(980_000, 8, 1.03), accounts: series(260, 8, 1.01).map(Math.round), note: null },
  { key: "insights", name: "Insights", color: "teal", arr: series(540_000, 8, 1.035), accounts: series(150, 8, 1.012).map(Math.round), note: null },
  { key: "payments", name: "Payments", color: "orange", arr: [null, null, 120_000, 168_000, 205_000, 262_000, 318_000, 402_000], accounts: [null, null, 22, 30, 38, 47, 58, 71], note: "Launched Mar 2026" },
  { key: "referrals", name: "Referrals", color: "success", arr: series(430_000, 8, 1.012), accounts: series(190, 8, 1.004).map(Math.round), note: null },
];

const TREND_MONTHS = monthsEndingAt(ANCHOR, 14);
const TREND = series(8_050_000, 14, 1.013);

const RET_MONTHS = monthsEndingAt(ANCHOR, 12);
const NRR = [105.2, 105.9, 106.4, 107.1, 106.8, 107.6, 108.2, 108.0, 108.9, 109.4, 109.1, 110.2];
const GDR = [93.1, 93.4, 92.9, 93.8, 94.1, 93.7, 94.4, 94.9, 94.6, 95.2, 95.0, 95.6];

/** The labeled sample payload. */
export const SAMPLE_DASHBOARD = {
  refreshed_at: "2026-07-18T06:30:00Z",
  arr: {
    trend: TREND_MONTHS.map((month, i) => ({ month, arr: TREND[i] })),
  },
  products: { months: PRODUCT_MONTHS, items: PRODUCTS },
  retention: {
    available: true,
    series: RET_MONTHS.map((month, i) => ({ month, nrr: NRR[i], gdr: GDR[i] })),
  },
  funnel: { won: 47, lost: 71, still_open: 19 },
} as unknown as KpiDashboardResponse;
